import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ExportSection = ({ svgRef }) => {
    const [isExportingPNG, setIsExportingPNG] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);

    const exportAsPNG = async () => {
        if (isExportingPNG || !svgRef) return;
        setIsExportingPNG(true);
        setExportProgress(0);
        
        // Start independent progress animation for 10 seconds
        // Use requestAnimationFrame for high priority
        let animationFrameId = null;
        const animationDuration = 10000; // 10 seconds
        const startTime = Date.now();
        let isCompleted = false;
        
        // Progress animation via requestAnimationFrame (high priority)
        const animateProgress = () => {
            if (isCompleted) {
                return;
            }
            
            const elapsed = Date.now() - startTime;
            const progress = Math.min((elapsed / animationDuration) * 95, 95);
            setExportProgress(Math.floor(progress));
            
            if (progress < 95 && !isCompleted) {
                animationFrameId = requestAnimationFrame(animateProgress);
            } else {
                isCompleted = true;
            }
        };
        
        // Start animation through multiple setTimeout calls to guarantee execution
        setTimeout(() => {
            setTimeout(() => {
                setTimeout(() => {
                    animationFrameId = requestAnimationFrame(animateProgress);
                }, 0);
            }, 0);
        }, 0);
        
        // Wait for several frames to let the browser render UI and start animation
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
        let svgElement = svgRef.current;
        
        const viewBox = svgElement.viewBox.baseVal;
        const originalWidth = viewBox.width;
        const originalHeight = viewBox.height;
        
        // Create a temporary container with fixed original size
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = originalWidth + 'px';
        tempContainer.style.height = originalHeight + 'px';
        tempContainer.style.backgroundColor = 'white';
        tempContainer.style.overflow = 'hidden';
        
        // Clone the entire SVG container and reset all transforms
        const svgClone = svgRef.current.cloneNode(true);
        const svgCloneElement = svgClone.querySelector('svg');
        
        if (svgCloneElement) {
            // Reset all transforms and positioning
            svgCloneElement.style.transform = 'none';
            svgCloneElement.style.position = 'static';
            svgCloneElement.style.left = '0';
            svgCloneElement.style.top = '0';
            svgCloneElement.style.width = originalWidth + 'px';
            svgCloneElement.style.height = originalHeight + 'px';
            svgCloneElement.style.display = 'block';
        }
        
        // Reset the container's transform as well
        svgClone.style.transform = 'none';
        svgClone.style.position = 'static';
        svgClone.style.left = '0';
        svgClone.style.top = '0';
        svgClone.style.width = originalWidth + 'px';
        svgClone.style.height = originalHeight + 'px';
        
        tempContainer.appendChild(svgClone);
        document.body.appendChild(tempContainer);
        
        // Wait a bit for the SVG to render
        await new Promise(resolve => setTimeout(resolve, 50));
        
        const canvas = await html2canvas(tempContainer, {
            backgroundColor: 'white',
            scale: 2,
            useCORS: true,
            allowTaint: true,
            width: originalWidth,
            height: originalHeight,
            logging: false,
            removeContainer: true,
            imageTimeout: 0
        });
        
        // Stop animation and set to 100%
        isCompleted = true;
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        setExportProgress(100);
        
        // Clean up
        document.body.removeChild(tempContainer);
        
        const link = document.createElement('a');
        link.download = 'Modelnaya.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
        } catch (error) {
        isCompleted = true;
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        console.error('Error exporting PNG:', error);
        } finally {
        setTimeout(() => {
            setIsExportingPNG(false);
            setExportProgress(0);
        }, 300);
        }
    };


    const exportAsPDF = async () => {
        if (isExportingPDF || !svgRef?.current) return;
        setIsExportingPDF(true);
        setExportProgress(0);
        
        // Start independent progress animation for 10 seconds
        // Use requestAnimationFrame for high priority
        let animationFrameId = null;
        const animationDuration = 10000; // 10 seconds
        const startTime = Date.now();
        let isCompleted = false;
        
        // Progress animation via requestAnimationFrame (high priority)
        const animateProgress = () => {
            if (isCompleted) {
                return;
            }
            
            const elapsed = Date.now() - startTime;
            const progress = Math.min((elapsed / animationDuration) * 95, 95);
            setExportProgress(Math.floor(progress));
            
            if (progress < 95 && !isCompleted) {
                animationFrameId = requestAnimationFrame(animateProgress);
            } else {
                isCompleted = true;
            }
        };
        
        // Start animation through multiple setTimeout calls to guarantee execution
        setTimeout(() => {
            setTimeout(() => {
                setTimeout(() => {
                    animationFrameId = requestAnimationFrame(animateProgress);
                }, 0);
            }, 0);
        }, 0);
        
        // Wait for several frames to let the browser render UI and start animation
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => requestAnimationFrame(resolve));
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
        let svgElement = svgRef.current.querySelector('svg');
        
        if (!svgElement && svgRef.current.tagName === 'svg') {
            svgElement = svgRef.current;
        }
        
        if (!svgElement) {
            svgElement = document.querySelector('svg');
        }
        
        if (!svgElement) {
            isCompleted = true;
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            console.error('SVG element not found anywhere');
            return;
        }
        
        const viewBox = svgElement.viewBox.baseVal;
        const originalWidth = viewBox.width;
        const originalHeight = viewBox.height;
        
        // Create a temporary container with fixed original size
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.left = '-9999px';
        tempContainer.style.top = '-9999px';
        tempContainer.style.width = originalWidth + 'px';
        tempContainer.style.height = originalHeight + 'px';
        tempContainer.style.backgroundColor = 'white';
        tempContainer.style.overflow = 'hidden';
        
        // Clone the entire SVG container and reset all transforms
        const svgClone = svgRef.current.cloneNode(true);
        const svgCloneElement = svgClone.querySelector('svg');
        
        if (svgCloneElement) {
            // Reset all transforms and positioning
            svgCloneElement.style.transform = 'none';
            svgCloneElement.style.position = 'static';
            svgCloneElement.style.left = '0';
            svgCloneElement.style.top = '0';
            svgCloneElement.style.width = originalWidth + 'px';
            svgCloneElement.style.height = originalHeight + 'px';
            svgCloneElement.style.display = 'block';
        }
        
        // Reset the container's transform as well
        svgClone.style.transform = 'none';
        svgClone.style.position = 'static';
        svgClone.style.left = '0';
        svgClone.style.top = '0';
        svgClone.style.width = originalWidth + 'px';
        svgClone.style.height = originalHeight + 'px';
        
        tempContainer.appendChild(svgClone);
        document.body.appendChild(tempContainer);
        
        // Wait a bit for the SVG to render
        await new Promise(resolve => setTimeout(resolve, 50));
        
        const canvas = await html2canvas(tempContainer, {
            backgroundColor: 'white',
            scale: 3,
            useCORS: true,
            allowTaint: true,
            width: originalWidth,
            height: originalHeight,
            logging: false,
            removeContainer: true,
            imageTimeout: 0
        });
        
        // Stop animation and set to 100%
        isCompleted = true;
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        setExportProgress(100);
        
        // Clean up
        document.body.removeChild(tempContainer);
        
        const imgData = canvas.toDataURL('image/jpeg', 0.85);
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [originalWidth, originalHeight]
        });

        pdf.addImage(imgData, 'JPEG', 0, 0, originalWidth, originalHeight);
        pdf.save('Modelnaya.pdf');
        } catch (error) {
        isCompleted = true;
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
        console.error('Error exporting PDF:', error);
        } finally {
        setTimeout(() => {
            setIsExportingPDF(false);
            setExportProgress(0);
        }, 300);
        }
    };

    return (
      <>
        <div className="flex-shrink-0 md:border-t md:border-gray-200 md:pt-3 md:mt-4">
          <h4 className="text-lg font-semibold text-gray-800 mb-2 hidden md:block">Скачать карту</h4>
          <div className="flex md:gap-2 items-stretch">
            <ExportButton
              onClick={exportAsPNG}
              disabled={isExportingPNG || isExportingPDF}
              loading={isExportingPNG}
              progress={isExportingPNG ? exportProgress : 0}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              }
              text="PNG"
              color="bg-blue-500"
            />
            <ExportButton
              onClick={exportAsPDF}
              disabled={isExportingPNG || isExportingPDF}
              loading={isExportingPDF}
              progress={isExportingPDF ? exportProgress : 0}
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
              }
              text="PDF"
              color="bg-red-500"
            />
          </div>
        </div>
      </>
    );
};
export default ExportSection;

const ExportButton = ({ onClick, disabled, loading, progress, icon, text, color, fullWidth = false }) => {
    return (
        <button
        onClick={onClick}
        disabled={disabled}
        className={`${fullWidth ? 'w-full' : 'flex-1'} relative px-3 py-2 text-xs font-bold ${color} text-white md:rounded hover:opacity-90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 overflow-hidden`}
        >
        {/* Progress bar covering full button height */}
        {loading && (
            <div 
                className="absolute inset-0 bg-white bg-opacity-30 transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
            />
        )}
        
        {/* Button content */}
        <span className="relative z-10 flex items-center justify-center gap-2">
            {loading ? (
                <span>ЗАГРУЗКА...</span>
            ) : (
                <>
                    {icon}
                    <span>{text}</span>
                </>
            )}
        </span>
        </button>
    );
};